import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { getAuth, isSuperAdmin } from '../../lib/auth';
import { MainLayout } from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  FileText, Search, Filter, Loader2, ChevronDown, ChevronUp, Eye,
  DollarSign, CheckCircle2, Clock, RefreshCw, X,
  Zap, Flame, Droplets, Wifi, Trash2, CreditCard, ArrowRightLeft,
} from 'lucide-react';

const COMMODITY_ICONS = {
  Electricity: { icon: Zap, color: 'text-amber-500' },
  Electric: { icon: Zap, color: 'text-amber-500' },
  Gas: { icon: Flame, color: 'text-orange-500' },
  Water: { icon: Droplets, color: 'text-blue-500' },
  Sewer: { icon: Droplets, color: 'text-indigo-500' },
  Telecom: { icon: Wifi, color: 'text-teal-500' },
  Internet: { icon: Wifi, color: 'text-teal-500' },
  Trash: { icon: Trash2, color: 'text-slate-500' },
  Waste: { icon: Trash2, color: 'text-slate-500' },
};

const formatCurrency = (v) => {
  if (v == null || v === 0) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
};

const formatDate = (d) => {
  if (!d) return '\u2014';
  try { return format(new Date(d), 'MMM d, yyyy'); } catch { return d; }
};

const formatShortDate = (d) => {
  if (!d) return '';
  try { return format(new Date(d), 'MM/dd/yy'); } catch { return d; }
};

/* ───── Stats Cards ──────────────────── */
const StatsBar = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Total Bills', value: (stats.total_bills || 0).toLocaleString(), icon: FileText, color: 'bg-blue-500/15 text-blue-600' },
    { label: 'In Payment File', value: (stats.in_pf_count || 0).toLocaleString(), icon: Clock, color: 'bg-amber-500/15 text-amber-600' },
    { label: 'Paid', value: (stats.paid_count || 0).toLocaleString(), sub: formatCurrency(stats.total_paid), icon: CheckCircle2, color: 'bg-emerald-500/15 text-emerald-600' },
    { label: 'Total Due', value: formatCurrency(stats.total_amount_due), icon: DollarSign, color: 'bg-violet-500/15 text-violet-600' },
  ];
  return (
    <div className="grid grid-cols-4 gap-4" data-testid="bills-stats">
      {cards.map(({ label, value, sub, icon: Icon, color }) => (
        <div key={label} className="flex items-center gap-3 p-4 border rounded-lg bg-card">
          <div className={`flex items-center justify-center h-10 w-10 rounded-md ${color}`}><Icon className="h-5 w-5" /></div>
          <div>
            <p className="text-lg font-bold leading-tight">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
            {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ───── Filter Bar with Labels + More Accordion ──── */
const FilterBar = ({ filters, filterValues, onFilterChange, onClear }) => {
  const [showMore, setShowMore] = useState(false);
  const hasActive = Object.values(filterValues).some(v => v);
  const activeCount = Object.values(filterValues).filter(v => v).length;

  return (
    <div className="border rounded-lg bg-muted/20" data-testid="bills-filters">
      {/* Primary filters — always visible */}
      <div className="flex flex-wrap items-end gap-3 p-3">
        <Filter className="h-4 w-4 text-muted-foreground mt-5" />

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Property</Label>
          <div className="relative w-48">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search property..."
              className="h-8 pl-7 text-xs"
              value={filterValues.property_search || ''}
              onChange={e => onFilterChange('property_search', e.target.value)}
              data-testid="filter-property"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Vendor</Label>
          <Select value={filterValues.vendor || '_all'} onValueChange={v => onFilterChange('vendor', v === '_all' ? '' : v)}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-vendor"><SelectValue placeholder="All Vendors" /></SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="_all">All Vendors</SelectItem>
              {(filters.vendors || []).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Commodity</Label>
          <Select value={filterValues.commodity || '_all'} onValueChange={v => onFilterChange('commodity', v === '_all' ? '' : v)}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-commodity"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All</SelectItem>
              {(filters.commodities || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Unit Type</Label>
          <Select value={filterValues.unit_type || '_all'} onValueChange={v => onFilterChange('unit_type', v === '_all' ? '' : v)}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-unit-type"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All</SelectItem>
              <SelectItem value="unit">Unit</SelectItem>
              <SelectItem value="common_area">Common Area</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Status</Label>
          <Select value={filterValues.payment_status || '_all'} onValueChange={v => onFilterChange('payment_status', v === '_all' ? '' : v)}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-payment-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="refund">Refund</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* More toggle */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs gap-1 text-muted-foreground hover:text-foreground"
          onClick={() => setShowMore(p => !p)}
          data-testid="filter-more-toggle"
        >
          {showMore ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          More{activeCount > 0 && !showMore ? ` (${activeCount})` : ''}
        </Button>

        {hasActive && (
          <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 text-red-500 hover:text-red-700" onClick={onClear} data-testid="filter-clear">
            <X className="h-3 w-3" /> Clear All
          </Button>
        )}
      </div>

      {/* Expanded filters */}
      {showMore && (
        <div className="flex flex-wrap items-end gap-3 px-3 pb-3 pt-1 border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">State</Label>
            <Select value={filterValues.state || '_all'} onValueChange={v => onFilterChange('state', v === '_all' ? '' : v)}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-state"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="_all">All States</SelectItem>
                {(filters.states || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Account #</Label>
            <Input
              placeholder="Search..."
              className="h-8 w-36 text-xs"
              value={filterValues.account_number || ''}
              onChange={e => onFilterChange('account_number', e.target.value)}
              data-testid="filter-account-number"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Issue Date From</Label>
            <Input
              type="date"
              className="h-8 w-36 text-xs"
              value={filterValues.date_from || ''}
              onChange={e => onFilterChange('date_from', e.target.value)}
              data-testid="filter-date-from"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Issue Date To</Label>
            <Input
              type="date"
              className="h-8 w-36 text-xs"
              value={filterValues.date_to || ''}
              onChange={e => onFilterChange('date_to', e.target.value)}
              data-testid="filter-date-to"
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ───── Payment Status Badge ─────────── */
const PaymentBadge = ({ bill }) => {
  const { payment_status, paid_amount, refund, refund_amount, transaction_reference } = bill;

  if (refund && ['yes', 'Yes', 'YES'].includes(refund)) {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px]" data-testid="badge-refund">
            Refund
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="text-xs">
          <p>Refunded: {formatCurrency(refund_amount)}</p>
          {transaction_reference && <p>Ref: {transaction_reference}</p>}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (payment_status === 'paid' || (paid_amount && paid_amount > 0)) {
    return (
      <Tooltip>
        <TooltipTrigger>
          <div className="flex flex-col items-start gap-0.5">
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px]" data-testid="badge-paid">
              Paid
            </Badge>
            {paid_amount > 0 && <span className="text-[10px] font-mono text-emerald-700">{formatCurrency(paid_amount)}</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent className="text-xs">
          <p>Amount: {formatCurrency(paid_amount)}</p>
          {transaction_reference && <p>Ref: {transaction_reference}</p>}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Badge variant="outline" className="bg-slate-500/10 text-slate-500 border-slate-500/20 text-[10px]" data-testid="badge-unpaid">
      Unpaid
    </Badge>
  );
};

const PF_STATUS_COLORS = {
  review: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  approved: 'bg-sky-500/10 text-sky-700 border-sky-500/30',
  paid: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
};

const PfStatusPill = ({ status }) => {
  if (!status) return null;
  const s = String(status).toLowerCase();
  const cls = PF_STATUS_COLORS[s] || 'bg-slate-500/10 text-slate-600 border-slate-500/30';
  return (
    <Badge variant="outline" className={`text-[9px] px-1 py-0 ${cls}`} data-testid="pf-status-pill">
      {s.charAt(0).toUpperCase() + s.slice(1)}
    </Badge>
  );
};

/* ───── Bill Row ─────────────────────── */
const BillRow = ({ bill, onView, onPay, onRefund }) => {
  const hasPf = !!bill.payment_file_id;
  const isPaid = bill.payment_status === 'paid' || (bill.paid_amount && bill.paid_amount > 0);
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = !isPaid && bill.due_date && bill.due_date < today;
  return (
    <TableRow
      className={`hover:bg-muted/50 transition-colors text-sm ${isOverdue ? 'bg-red-50/40' : ''}`}
      data-testid={`bill-row-${bill.bill_id}`}
    >
      {/* Property + Service Address */}
      <TableCell className="max-w-[220px]">
        <div className="flex flex-col">
          <span className="font-medium text-xs truncate" title={bill.primary_service_address}>
            {bill.primary_service_address || bill.customer_full_name || '\u2014'}
          </span>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-[10px] text-muted-foreground truncate">
              {bill.primary_service_city}{bill.primary_service_state ? `, ${bill.primary_service_state}` : ''} {bill.primary_service_zip}
            </span>
            {bill.is_unit_bill ? (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 bg-indigo-500/10 text-indigo-600 border-indigo-500/20">Unit</Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Unit-level bill</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 bg-slate-500/10 text-slate-500 border-slate-500/20">CA</Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Common Area</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </TableCell>
      {/* Dates */}
      <TableCell className="text-xs whitespace-nowrap">
        <div className="flex flex-col gap-0.5">
          <span>{formatShortDate(bill.invoice_date)}</span>
          <div className="flex items-center gap-1">
            <span className={`text-[10px] ${isOverdue ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
              Due {formatShortDate(bill.due_date)}
            </span>
            {isOverdue && (
              <Badge variant="outline" className="text-[9px] px-1 py-0 bg-red-500/10 text-red-700 border-red-500/30" data-testid={`overdue-badge-${bill.bill_id}`}>
                Overdue
              </Badge>
            )}
          </div>
        </div>
      </TableCell>
      {/* Period */}
      <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">
        {bill.period_from && bill.period_to ? (
          <span>{formatShortDate(bill.period_from)} — {formatShortDate(bill.period_to)}</span>
        ) : '\u2014'}
      </TableCell>
      {/* PF Batch */}
      <TableCell>
        {bill.pf_batch_name ? (
          <div className="flex flex-col items-start gap-0.5">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-500/10 text-blue-600 border-blue-500/20" data-testid={`bill-pf-${bill.bill_id}`}>
              {bill.pf_batch_name}
            </Badge>
            <PfStatusPill status={bill.pf_status} />
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground">Not in PF</span>
        )}
      </TableCell>
      {/* Vendor + Account */}
      <TableCell className="max-w-[160px]">
        <div className="flex flex-col">
          <span className="text-xs font-medium truncate" title={bill.vendor_name}>{bill.vendor_name || '\u2014'}</span>
          <span className="text-[10px] text-muted-foreground font-mono truncate" title={bill.account_number}>{bill.account_number}</span>
        </div>
      </TableCell>
      {/* Commodity */}
      <TableCell className="text-xs">
        {(() => {
          const cm = bill.commodity || '';
          const match = Object.entries(COMMODITY_ICONS).find(([k]) => cm.toLowerCase().includes(k.toLowerCase()));
          if (match) {
            const [, { icon: Icon, color }] = match;
            return <span className="flex items-center gap-1"><Icon className={`h-3.5 w-3.5 ${color}`} />{cm}</span>;
          }
          return cm || '\u2014';
        })()}
      </TableCell>
      {/* New Charges */}
      <TableCell className="text-xs font-mono text-right">{formatCurrency(bill.current_bill_amount)}</TableCell>
      {/* Total Due */}
      <TableCell className="text-xs font-mono text-right font-medium">{formatCurrency(bill.total_amount_due)}</TableCell>
      {/* Payment Status */}
      <TableCell><PaymentBadge bill={bill} /></TableCell>
      {/* Actions */}
      <TableCell>
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onView(bill)} data-testid={`bill-view-${bill.bill_id}`}>
                <Eye className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="text-xs">View Bill</TooltipContent>
          </Tooltip>
          {hasPf && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => onPay(bill)} className="p-1 rounded hover:bg-emerald-100 transition-colors" data-testid={`bill-pay-${bill.bill_id}`}>
                    <CreditCard className="h-3.5 w-3.5 text-emerald-600" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="text-xs">{bill.payment_status === 'paid' ? 'View Payment' : 'Record Payment'}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => onRefund(bill)} className="p-1 rounded hover:bg-red-100 transition-colors" data-testid={`bill-refund-${bill.bill_id}`}>
                    <ArrowRightLeft className="h-3.5 w-3.5 text-red-500" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Record Refund</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

/* ───── Main Page ────────────────────── */
export default function BillsPage() {
  const { user } = getAuth();
  const navigate = useNavigate();

  const [currentAccount, setCurrentAccount] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });

  useEffect(() => {
    const check = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (acc?.id && acc.id !== currentAccount?.id) setCurrentAccount(acc);
    };
    check();
    const iv = setInterval(check, 1000);
    const to = setTimeout(() => clearInterval(iv), 10000);
    return () => { clearInterval(iv); clearTimeout(to); };
  }, [currentAccount?.id]);

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ vendors: [], commodities: [], states: [], properties: [] });
  const [searchParams] = useSearchParams();
  const [filterValues, setFilterValues] = useState(() => {
    const init = {};
    ['property_search', 'account_number', 'vendor', 'commodity', 'state', 'unit_type', 'payment_status', 'date_from', 'date_to'].forEach(k => {
      const v = searchParams.get(k);
      if (v) init[k] = v;
    });
    return init;
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const limit = 50;

  // Payment drawer state
  const [payDrawerOpen, setPayDrawerOpen] = useState(false);
  const [payBill, setPayBill] = useState(null);
  const [payForm, setPayForm] = useState({});
  const [paySaving, setPaySaving] = useState(false);
  const [paySummary, setPaySummary] = useState(null);
  const [paySummaryLoading, setPaySummaryLoading] = useState(false);

  // Refund drawer state
  const [refundDrawerOpen, setRefundDrawerOpen] = useState(false);
  const [refundBill, setRefundBill] = useState(null);
  const [refundForm, setRefundForm] = useState({});
  const [refundSaving, setRefundSaving] = useState(false);

  const userId = user?.id || user?.user_id || '';
  const userName = user?.name || '';

  const accountId = currentAccount?.id;

  // Fetch filters once
  useEffect(() => {
    if (!accountId) return;
    api.get('/bills/filters', { params: { account_id: accountId } })
      .then(({ data }) => setFilters(data))
      .catch(() => {});
  }, [accountId]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await api.get('/bills/stats', { params: { account_id: accountId } });
      setStats(data);
    } catch { /* silent */ }
  }, [accountId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Fetch bills
  const fetchBills = useCallback(async (pageNum = 1, append = false) => {
    if (!accountId) return;
    if (!append) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = { account_id: accountId, page: pageNum, limit };
      Object.entries(filterValues).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get('/bills/list', { params });
      if (append) {
        setBills(prev => [...prev, ...(data.bills || [])]);
      } else {
        setBills(data.bills || []);
      }
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
      setPage(pageNum);
    } catch {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [accountId, filterValues]);

  useEffect(() => { fetchBills(1); }, [fetchBills]);

  const handleFilterChange = (key, value) => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilterValues({});
  };

  const handleLoadMore = () => {
    fetchBills(page + 1, true);
  };

  const handleView = (bill) => {
    navigate(`/bills/${bill.bill_id}?account_id=${accountId}`);
  };

  // ── Payment drawer ──
  const openPayDrawer = async (bill) => {
    if (!bill.payment_file_id) {
      toast.error('This bill is not in a Payment File. Pay/Refund is only available for PF bills.');
      return;
    }
    setPayBill(bill);
    setPayForm({
      transaction_reference: '',
      amount: bill.total_amount_due || '',
      payment_method: '',
      notes: '',
      transaction_date: new Date().toISOString().split('T')[0],
    });
    setPayDrawerOpen(true);
    setPaySummary(null);
    setPaySummaryLoading(true);
    try {
      const { data } = await api.get('/payments/v2/workspace/bill-payment-summary', {
        params: { bill_id: bill.bill_id, payment_file_id: bill.payment_file_id, account_id: bill.account_id },
      });
      setPaySummary(data);
      // Pre-fill remaining amount if partially paid
      if (data.remaining > 0.01) {
        setPayForm(p => ({ ...p, amount: data.remaining.toFixed(2) }));
      }
    } catch { /* silent */ }
    finally { setPaySummaryLoading(false); }
  };

  const savePayment = async () => {
    if (!payForm.transaction_reference?.trim()) { toast.error('Transaction reference required'); return; }
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { toast.error('Valid amount required'); return; }

    // Full-payment confirmation
    try {
      const { data: latest } = await api.get('/payments/v2/workspace/bill-payment-summary', {
        params: { bill_id: payBill.bill_id, payment_file_id: payBill.payment_file_id, account_id: payBill.account_id },
      });
      setPaySummary(latest);
      if (latest.is_fully_paid) {
        const ok = window.confirm(
          `This bill is already fully paid (${(latest.txn_sum || 0).toFixed(2)} of ${(latest.payment_amount || 0).toFixed(2)} via ${latest.transaction_count} transaction(s)).\n\nAre you sure you want to record ANOTHER transaction?`
        );
        if (!ok) return;
      }
    } catch { /* continue */ }

    setPaySaving(true);
    try {
      const utilRes = await api.get(`/payments/v2/files/${payBill.payment_file_id}/utilities`, {
        params: { account_id: payBill.account_id, account_number: payBill.account_number, limit: 10 }
      });
      const matchUtil = (utilRes.data?.utilities || []).find(u => u.bill_id === payBill.bill_id);
      if (!matchUtil) { toast.error('Could not find utility record for this bill'); setPaySaving(false); return; }

      await api.post(`/payments/v2/files/${payBill.payment_file_id}/record-payment`, {
        account_id: payBill.account_id,
        utility_id: matchUtil.payment_file_utility_id,
        transaction_reference: payForm.transaction_reference,
        amount: parseFloat(payForm.amount),
        payment_method: payForm.payment_method || '',
        notes: payForm.notes || '',
        paid_by: userId,
        paid_by_name: userName,
        created_by: userId,
        created_by_name: userName,
        transaction_date: payForm.transaction_date || '',
      });
      toast.success('Payment recorded');
      setPayDrawerOpen(false);
      setPayBill(null);
      fetchBills(1);
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
    finally { setPaySaving(false); }
  };

  // ── Refund drawer ──
  const openRefundDrawer = (bill) => {
    if (!bill.payment_file_id) {
      toast.error('This bill is not in a Payment File. Pay/Refund is only available for PF bills.');
      return;
    }
    setRefundBill(bill);
    setRefundForm({
      refund_amount: '',
      refund_method: '',
      refund_date: new Date().toISOString().split('T')[0],
    });
    setRefundDrawerOpen(true);
  };

  const saveRefund = async () => {
    if (!refundForm.refund_amount || parseFloat(refundForm.refund_amount) <= 0) { toast.error('Valid refund amount required'); return; }
    setRefundSaving(true);
    try {
      const utilRes = await api.get(`/payments/v2/files/${refundBill.payment_file_id}/utilities`, {
        params: { account_id: refundBill.account_id, account_number: refundBill.account_number, limit: 10 }
      });
      const matchUtil = (utilRes.data?.utilities || []).find(u => u.bill_id === refundBill.bill_id);
      if (!matchUtil) { toast.error('Could not find utility record'); setRefundSaving(false); return; }

      await api.post(`/payments/v2/files/${refundBill.payment_file_id}/record-refund`, {
        account_id: refundBill.account_id, utility_id: matchUtil.payment_file_utility_id,
        refund_amount: parseFloat(refundForm.refund_amount),
        refunded_by: userName || '3rd Party Payment',
        refund_method: refundForm.refund_method || '',
        refund_date: refundForm.refund_date || new Date().toISOString().split('T')[0],
        user_id: userId, user_name: userName,
        created_by: userId, created_by_name: userName,
      });
      toast.success('Refund recorded');
      setRefundDrawerOpen(false);
      setRefundBill(null);
      fetchBills(1);
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record refund'); }
    finally { setRefundSaving(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/bills/sync/trigger', null, { params: { mode: 'incremental' } });
      toast.success(`Bill sync complete: ${data.bills_created} new, ${data.bills_updated} updated`);
      fetchBills(1);
      fetchStats();
    } catch {
      toast.error('Bill sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (!accountId) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <FileText className="h-12 w-12 mb-4 opacity-40" />
          <p className="text-sm font-medium">No account selected</p>
          <p className="text-xs mt-1">Use the account switcher in the header to select an organization</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout wide>
      <TooltipProvider delayDuration={200}>
      <div className="space-y-5" data-testid="bills-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-foreground" strokeWidth={2} />
            <h1 className="text-2xl font-bold tracking-tight">Bills</h1>
            <span className="text-sm text-muted-foreground">
              {total.toLocaleString()} bills
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin() && (
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleSync} disabled={syncing} data-testid="bills-sync-btn">
                <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Bills'}
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <StatsBar stats={stats} />

        {/* Filters */}
        <FilterBar
          filters={filters}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          onClear={handleClearFilters}
        />

        {/* Table */}
        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="bills-table-container">
          {loading ? (
            <div className="space-y-3 p-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : bills.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <FileText className="h-12 w-12 mb-3 opacity-40" />
              <p className="text-sm">No bills found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table data-testid="bills-table">
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[220px]">Property / Address</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Issue / Due</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Period</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">PF Batch</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[160px]">Vendor / Account</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Commodity</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-right">New Charges</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-right">Total Due</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Payment</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[90px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bills.map(b => <BillRow key={b.bill_id} bill={b} onView={handleView} onPay={openPayDrawer} onRefund={openRefundDrawer} />)}
                  </TableBody>
                </Table>
              </div>
              {/* Load More */}
              {hasMore && (
                <div className="flex justify-center py-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs gap-1.5"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    data-testid="bills-load-more"
                  >
                    {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    {loadingMore ? 'Loading...' : `Load More (${bills.length} of ${total})`}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </TooltipProvider>

      {/* Payment Drawer */}
      <Sheet open={payDrawerOpen} onOpenChange={(o) => { if (!o) { setPayDrawerOpen(false); setPayBill(null); } }}>
        <SheetContent side="right" className="sm:max-w-md w-[420px] flex flex-col p-0" data-testid="pay-drawer">
          <SheetHeader className="px-5 pt-5 pb-3 border-b">
            <SheetTitle className="text-sm flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-emerald-600" />
              Record Payment
            </SheetTitle>
          </SheetHeader>
          {payBill && (
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div className="bg-muted/30 rounded p-3 text-xs space-y-1">
                <p className="font-medium">{payBill.vendor_name} — {payBill.account_number}</p>
                <p className="text-muted-foreground">{payBill.primary_service_address}</p>
                <p>Total Due: <strong>{formatCurrency(payBill.total_amount_due)}</strong></p>
              </div>

              {/* Payment summary (stored vs computed) */}
              {paySummaryLoading ? (
                <div className="flex items-center justify-center py-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 mr-2 animate-spin" /> Loading payment history…</div>
              ) : paySummary && (
                <div className="border rounded-lg p-3 text-xs space-y-1.5 bg-muted/20" data-testid="bills-pay-summary">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-[10px] uppercase tracking-wider font-semibold">Stored / Computed</span>
                    <div className="flex gap-1">
                      <Badge variant="outline" className="text-[10px]">{paySummary.stored_status}</Badge>
                      <Badge variant="outline" className="text-[10px]">{paySummary.computed_status}</Badge>
                    </div>
                  </div>
                  <div className="text-muted-foreground">
                    Paid <span className="font-mono font-semibold text-emerald-700">{formatCurrency(paySummary.txn_sum)}</span> of {formatCurrency(paySummary.payment_amount)}
                    {paySummary.remaining > 0.01 && <> · Remaining <span className="font-mono text-amber-700">{formatCurrency(paySummary.remaining)}</span></>}
                  </div>
                  {paySummary.status_mismatch && (
                    <div className="text-amber-700 text-[11px] pt-1 border-t">⚠ Stored status disagrees with transactions</div>
                  )}
                  {paySummary.transactions && paySummary.transactions.length > 0 && (
                    <div className="pt-1.5 border-t space-y-1">
                      {paySummary.transactions.map((t, i) => (
                        <div key={t.id || i} className="flex items-center justify-between text-[11px]">
                          <span className="font-mono text-emerald-700">#{i + 1} {formatCurrency(t.amount)}</span>
                          <span className="text-muted-foreground truncate ml-2">{t.paid_by_name || '—'} · {t.transaction_reference || '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Transaction Reference *</Label>
                  <Input className="h-9 text-sm mt-1" value={payForm.transaction_reference || ''} onChange={e => setPayForm(p => ({...p, transaction_reference: e.target.value}))} placeholder="e.g. CHK-12345" data-testid="pay-txn-ref" />
                </div>
                <div>
                  <Label className="text-xs">Amount *</Label>
                  <Input className="h-9 text-sm mt-1 font-mono" type="number" step="0.01" value={payForm.amount || ''} onChange={e => setPayForm(p => ({...p, amount: e.target.value}))} data-testid="pay-amount" />
                </div>
                <div>
                  <Label className="text-xs">Payment Method</Label>
                  <Input className="h-9 text-sm mt-1" value={payForm.payment_method || ''} onChange={e => setPayForm(p => ({...p, payment_method: e.target.value}))} placeholder="ACH, Check, Wire" data-testid="pay-method" />
                </div>
                <div>
                  <Label className="text-xs">Transaction Date</Label>
                  <Input className="h-9 text-sm mt-1" type="date" value={payForm.transaction_date || ''} onChange={e => setPayForm(p => ({...p, transaction_date: e.target.value}))} data-testid="pay-date" />
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Input className="h-9 text-sm mt-1" value={payForm.notes || ''} onChange={e => setPayForm(p => ({...p, notes: e.target.value}))} placeholder="Optional notes" data-testid="pay-notes" />
                </div>
              </div>
            </div>
          )}
          <div className="border-t px-5 py-3 flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => { setPayDrawerOpen(false); setPayBill(null); }}>Cancel</Button>
            <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={savePayment} disabled={paySaving} data-testid="pay-save-btn">
              {paySaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CreditCard className="h-3 w-3 mr-1" />}
              Save Payment
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Refund Drawer */}
      <Sheet open={refundDrawerOpen} onOpenChange={(o) => { if (!o) { setRefundDrawerOpen(false); setRefundBill(null); } }}>
        <SheetContent side="right" className="sm:max-w-md w-[420px] flex flex-col p-0" data-testid="refund-drawer">
          <SheetHeader className="px-5 pt-5 pb-3 border-b">
            <SheetTitle className="text-sm flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 text-red-500" />
              Record Refund
            </SheetTitle>
          </SheetHeader>
          {refundBill && (
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div className="bg-muted/30 rounded p-3 text-xs space-y-1">
                <p className="font-medium">{refundBill.vendor_name} — {refundBill.account_number}</p>
                <p className="text-muted-foreground">{refundBill.primary_service_address}</p>
                <p>Total Due: <strong>{formatCurrency(refundBill.total_amount_due)}</strong></p>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Refund Amount *</Label>
                  <Input className="h-9 text-sm mt-1 font-mono" type="number" step="0.01" value={refundForm.refund_amount || ''} onChange={e => setRefundForm(p => ({...p, refund_amount: e.target.value}))} data-testid="refund-amount" />
                </div>
                <div>
                  <Label className="text-xs">Refund Method</Label>
                  <Input className="h-9 text-sm mt-1" value={refundForm.refund_method || ''} onChange={e => setRefundForm(p => ({...p, refund_method: e.target.value}))} placeholder="ACH, Check, Wire" data-testid="refund-method" />
                </div>
                <div>
                  <Label className="text-xs">Refund Date</Label>
                  <Input className="h-9 text-sm mt-1" type="date" value={refundForm.refund_date || ''} onChange={e => setRefundForm(p => ({...p, refund_date: e.target.value}))} data-testid="refund-date" />
                </div>
              </div>
            </div>
          )}
          <div className="border-t px-5 py-3 flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => { setRefundDrawerOpen(false); setRefundBill(null); }}>Cancel</Button>
            <Button size="sm" className="text-xs bg-red-600 hover:bg-red-700 text-white" onClick={saveRefund} disabled={refundSaving} data-testid="refund-save-btn">
              {refundSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ArrowRightLeft className="h-3 w-3 mr-1" />}
              Save Refund
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </MainLayout>
  );
}
